import { acMobileFeatures } from '@arnoldclark/ac-mobile-features';

import Auth from '@lib/auth';
import useModal from '@javascript/lib/hooks/useModal';

import classNames from 'classnames';

import type { Endpoint } from '@javascript/types/Requests';

const StockAlertLauncher = ({
  additionalStyling,
  analyticsLocation,
  stockAlertUrl = '/stock-alerts/new',
}: {
  additionalStyling?: string;
  analyticsLocation?: string;
  stockAlertUrl?: Endpoint;
}) => {
  const { openModal, closeModal, ref, maskClickHandlers } = useModal();

  const handleCreateClick = async () => {
    //app users shouldn't see any option to log in
    //and will immediately be directed to stock alerts regardless of logged-in status
    const { isNativeApp } = acMobileFeatures();
    if (isNativeApp()) {
      window.location.href = stockAlertUrl;
      return;
    }

    const isLoggedIn = await new Auth()
      .initializeAuthClient(window.authOptions)
      .then((client) => client.isAuthenticated());

    //if the user is already logged in, no need to prompt them again
    if (isLoggedIn) {
      window.location.href = stockAlertUrl;
      return;
    }

    //otherwise, show the options to log in or continue as guest
    openModal();
  };

  const login = () =>
    new Auth()
      .initializeAuthClient(window.authOptions)
      .then((client) => client.login(stockAlertUrl));

  return (
    <>
      <button
        className={classNames('ch-btn js-off--hide', additionalStyling)}
        data-gtm-track="interaction"
        data-gtm-track-action={`Opened stock alerts wizard${
          analyticsLocation ? ` - ${analyticsLocation}` : ''
        }`}
        onClick={handleCreateClick}
        type="button"
      >
        Create a stock alert
      </button>
      <dialog {...maskClickHandlers} ref={ref}>
        <div className="ch-modal ch-modal--drawer ac-stock-alerts__launcher ac-modal__close--grey ch-bg--grey-1 ch-text--left ch-ph--3 ch-pv--4 sm:ch-ph--6 sm:ch-pv--7">
          <header>
            <h1 className="ch-fs--5 sm:ch-fs--6 ch-mb--4 sm:ch-mb--5">
              Create a stock alert
            </h1>
            <button
              className="ch-modal__close"
              onClick={closeModal}
              type="button"
            >
              <span className="ch-reader">Close</span>
            </button>
          </header>
          <main>
            <div className="ch-bg--white ch-rounded--md ch-shadow--sm">
              <div className="ch-pa--3 sm:ch-pv--4">
                <h2 className="ch-fs--4 sm:ch-fs--5">Log in to your account</h2>
                <p className="ch-body ch-color--grey-6 ch-mb--3 sm:ch-mb--4">
                  You&apos;ll be able to manage all of your stock alerts from
                  your account.
                </p>
                <button
                  className="ch-btn ch-btn--success ch-width--12"
                  onClick={login}
                  type="button"
                >
                  Log in to my account
                </button>
              </div>
            </div>
            <span className="ch-text-divider ch-fs--3 ch-color--grey-6 ch-mv--3 sm:ch-mv--4 ch-fw--400 ch-bc--grey-3">
              Or
            </span>
            <a
              className="ch-btn ch-width--12 ch-bc--grey-6"
              href={stockAlertUrl}
              rel="nofollow noindex"
            >
              Continue without logging in
            </a>
          </main>
        </div>
      </dialog>
    </>
  );
};

export default StockAlertLauncher;
